import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Sparkles, Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">PostToSocial</span>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Forgot password?</h1>
          <p className="text-zinc-500 mt-2">
            {submitted ? 'Check your inbox for next steps' : "We'll send you a link to reset it"}
          </p>
        </div>

        <Card className="border-zinc-200 shadow-sm animate-fade-in" style={{ animationDelay: '0.1s' }}>
          {submitted ? (
            <CardContent className="pt-6 pb-6 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-zinc-900 mb-2" data-testid="forgot-success-title">
                Email sent
              </h2>
              <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
                If an account exists for <span className="font-medium text-zinc-900">{email}</span>,
                we've sent password reset instructions. The link expires in 1 hour.
              </p>
              <p className="text-xs text-zinc-500 mb-6">
                Didn't receive it? Check your spam folder, or try again with a different email.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setSubmitted(false); setEmail(''); }}
                data-testid="forgot-try-again-btn"
              >
                Try a different email
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">Reset your password</CardTitle>
                <CardDescription>Enter the email associated with your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Send reset link
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-sm text-zinc-500 mt-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};
